import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { IsAuthResponseData } from '../interfaces/auth-response-data';
import { IsUser } from '../interfaces/is-user';
import { Router } from '@angular/router';
import { User } from '../classes/user';
import { StorageService } from './storage.service';
import { StorageKeys } from '../types/storage-keys';
import { Endpoints, EndpointType } from '../types/endpoints';
import { RouteSegments } from '../types/route-segments';
import { PlatformService } from './platform.service';
import { IsAccountRequestForm } from '../interfaces/is-account-request-form';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	// Url settings
	private readonly api: string = environment.api;
	private readonly authEndpoint: EndpointType = Endpoints.AUTH;
	private readonly authRequestEndpoint: EndpointType = Endpoints.AUTH_REQUEST;
	// Injections
	private readonly http: HttpClient = inject(HttpClient);
	private readonly router: Router = inject(Router);
	private readonly storageService: StorageService = inject(StorageService);
	private readonly platformService: PlatformService = inject(PlatformService);
	// User state
	private user$: BehaviorSubject<IsUser | undefined> = new BehaviorSubject<IsUser | undefined>(undefined);

	public get user(): Observable<IsUser | undefined> {
		return this.user$.asObservable();
	}

	/**
	 * Login user
	 * @param username
	 * @param password
	 */
	public login(username: string, password: string): Observable<IsAuthResponseData> {
		return this.http
			.post<IsAuthResponseData>(`${this.api}/${this.authEndpoint}`, {
				username,
				password,
			})
			.pipe(
				tap((resData: IsAuthResponseData) => {
					this.handleAuthentication(username, resData.accessToken, resData.options);
				})
			);
	}

	/**
	 * Auto login user using session storage
	 */
	public autoLogin() {
		if (this.platformService.isBrowser()) {
			// Try to get user form session storage
			const user: IsUser | undefined = this.storageService.getFromSessionStorage<IsUser>(StorageKeys.USER);
			if (user && user.token) {
				// Set user to BehaviorSubject
				this.user$.next(user);
			}
		}
	}

	/**
	 * Logout user
	 */
	public logout(): void {
		this.user$.next(undefined);
		this.router.navigate([RouteSegments.LOGIN]);
		this.storageService.removeFromSessionStorage(StorageKeys.USER);
	}

	/**
	 * Request account
	 * @param requestForm
	 * @param recaptchaToken
	 */
	public requestAccount(requestForm: IsAccountRequestForm, recaptchaToken: string): Observable<void> {
		return this.http.post<void>(`${this.api}/${this.authRequestEndpoint}`, { requestForm, recaptchaToken });
	}

	/**
	 * Set user to BehaviorSubject and session storage
	 * @param email
	 * @param token
	 * @param options
	 * @private
	 */
	private handleAuthentication(email: string, token: string, options: string[]) {
		const user: IsUser = new User(email, token, options);
		this.user$.next(user);
		this.storageService.setToSessionStorage(StorageKeys.USER, user);
	}

	/**
	 * Get recaptcha token
	 */
	public getRecaptchaToken(): Promise<string> {
		const siteKey: string = environment.recaptchaSiteKey;
		return new Promise<string>(resolve => {
			grecaptcha.ready(() => {
				grecaptcha.execute(siteKey, { action: 'submit' }).then((token: string) => {
					resolve(token);
				});
			});
		});
	}
}
